/**
 * 2D Canvas for warehouse layout editing
 * Supports multi-select, rubber band selection, and bulk operations
 */

import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import {
  Layout2DCanvasProps,
  RackPosition,
  RackDimensions,
  PreviewRackPosition,
  ACTIVITY_COLORS,
  ActivityType,
  DEFAULT_RUBBER_BAND
} from '../types/warehouseLayout';
import {
  snapToGrid,
  screenToWorld,
  getRotatedDimensions,
  calculateRackDimensions
} from '../utils/positionCalculator';
import {
  isMultiSelectKey,
  isRangeSelectKey,
  getRacksInArea,
  toggleSelection,
  singleSelect,
  rangeSelect,
  startRubberBand,
  updateRubberBand,
  resetRubberBand
} from '../utils/selectionUtils';

interface DragState {
  isDragging: boolean;
  draggedRacks: string[];
  startPositions: Map<string, { x: number; y: number }>;
  mouseStartX: number;
  mouseStartY: number;
}

const Layout2DCanvas: React.FC<Layout2DCanvasProps> = ({
  layout,
  editorState,
  rackSummaries,
  onLayoutChange,
  onEditorStateChange,
  onSelectionChange,
  previewPositions = [],
  heatmapData = [],
  activities = []
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    draggedRacks: [],
    startPositions: new Map(),
    mouseStartX: 0,
    mouseStartY: 0
  });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [lastClickedRack, setLastClickedRack] = useState<string | null>(null);

  // Calculate rack dimensions from summaries
  const rackDimensionsMap = useMemo(() => {
    const map = new Map<string, RackDimensions>();
    rackSummaries.forEach(summary => {
      map.set(summary.rackNumber, calculateRackDimensions(summary));
    });
    return map;
  }, [rackSummaries]);

  // Create heatmap lookup
  const heatmapLookup = useMemo(() => {
    const lookup = new Map<string, number>();
    heatmapData.forEach(data => {
      lookup.set(data.rackNumber, Math.max(lookup.get(data.rackNumber) || 0, data.intensity));
    });
    return lookup;
  }, [heatmapData]);

  // Create activity lookup
  const activityLookup = useMemo(() => {
    const lookup = new Map<string, typeof activities>();
    activities.forEach(activity => {
      const key = activity.rackNumber;
      if (!lookup.has(key)) {
        lookup.set(key, []);
      }
      lookup.get(key)!.push(activity);
    });
    return lookup;
  }, [activities]);

  // Handle canvas resize
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setCanvasSize({
          width: rect.width,
          height: rect.height
        });
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Select all (Ctrl+A)
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        const allRackNumbers = new Set(layout.racks.map(r => r.rackNumber));
        onSelectionChange(allRackNumbers);
      }

      // Deselect all (Escape)
      if (e.key === 'Escape') {
        onSelectionChange(new Set());
        if (editorState.rubberBand.isActive) {
          onEditorStateChange({
            ...editorState,
            rubberBand: resetRubberBand()
          });
        }
      }

      // Delete selected (Delete)
      if (e.key === 'Delete' && editorState.selectedRacks.size > 0) {
        const remainingRacks = layout.racks.filter(
          rack => !editorState.selectedRacks.has(rack.rackNumber)
        );
        onLayoutChange({ ...layout, racks: remainingRacks });
        onSelectionChange(new Set());
      }

      // Rotate selected (R)
      if (e.key === 'r' && editorState.selectedRacks.size > 0 && !e.ctrlKey && !e.metaKey) {
        const updatedRacks = layout.racks.map(rack => {
          if (editorState.selectedRacks.has(rack.rackNumber)) {
            return {
              ...rack,
              rotation: ((rack.rotation + 90) % 360) as 0 | 90 | 180 | 270
            };
          }
          return rack;
        });
        onLayoutChange({ ...layout, racks: updatedRacks });
      }

      // Align to grid (G)
      if (e.key === 'g' && editorState.selectedRacks.size > 0 && !e.ctrlKey && !e.metaKey) {
        const updatedRacks = layout.racks.map(rack => {
          if (editorState.selectedRacks.has(rack.rackNumber)) {
            return {
              ...rack,
              x: snapToGrid(rack.x, layout.gridUnit),
              y: snapToGrid(rack.y, layout.gridUnit)
            };
          }
          return rack;
        });
        onLayoutChange({ ...layout, racks: updatedRacks });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editorState, layout, onLayoutChange, onEditorStateChange, onSelectionChange]);

  // Draw canvas
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { width, height } = canvasSize;
    const { dimensions, racks, gridUnit } = layout;
    const { zoom, panOffset, showGrid, showLabels, showHeatmap, selectedRacks, anchorRack, rubberBand } = editorState;

    // Calculate scale to fit warehouse in canvas
    const scale = Math.min(
      (width - 40) / dimensions.length,
      (height - 40) / dimensions.width
    ) * zoom;

    const offsetX = (width - dimensions.length * scale) / 2 + panOffset.x;
    const offsetY = (height - dimensions.width * scale) / 2 + panOffset.y;

    // Clear canvas
    ctx.fillStyle = '#1f2937';
    ctx.fillRect(0, 0, width, height);

    // Draw warehouse floor
    ctx.fillStyle = '#374151';
    ctx.fillRect(offsetX, offsetY, dimensions.length * scale, dimensions.width * scale);

    // Draw grid
    if (showGrid) {
      ctx.strokeStyle = '#4b5563';
      ctx.lineWidth = 0.5;
      ctx.beginPath();

      for (let x = 0; x <= dimensions.length; x += gridUnit) {
        const screenX = offsetX + x * scale;
        ctx.moveTo(screenX, offsetY);
        ctx.lineTo(screenX, offsetY + dimensions.width * scale);
      }

      for (let y = 0; y <= dimensions.width; y += gridUnit) {
        const screenY = offsetY + y * scale;
        ctx.moveTo(offsetX, screenY);
        ctx.lineTo(offsetX + dimensions.length * scale, screenY);
      }

      ctx.stroke();
    }

    // Draw dimension labels
    ctx.fillStyle = '#9ca3af';
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`${dimensions.length}m`, offsetX + (dimensions.length * scale) / 2, offsetY - 8);
    ctx.save();
    ctx.translate(offsetX - 8, offsetY + (dimensions.width * scale) / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText(`${dimensions.width}m`, 0, 0);
    ctx.restore();

    // Draw preview positions (ghost racks)
    previewPositions.forEach(preview => {
      const dim = rackDimensionsMap.get(preview.rackNumber) || {
        width: 2.5, depth: 1.0, height: 3.0, verticals: 5, shelves: 5
      };

      const rotated = getRotatedDimensions(dim, preview.rotation);
      const screenX = offsetX + preview.x * scale;
      const screenY = offsetY + preview.y * scale;
      const screenWidth = rotated.width * scale;
      const screenDepth = rotated.depth * scale;

      // Ghost rack with dashed border
      ctx.fillStyle = preview.isValid ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)';
      ctx.fillRect(
        screenX - screenWidth / 2,
        screenY - screenDepth / 2,
        screenWidth,
        screenDepth
      );

      ctx.strokeStyle = preview.isValid ? '#22c55e' : '#ef4444';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(
        screenX - screenWidth / 2,
        screenY - screenDepth / 2,
        screenWidth,
        screenDepth
      );
      ctx.setLineDash([]);

      // Label
      ctx.fillStyle = preview.isValid ? '#22c55e' : '#ef4444';
      ctx.font = 'bold 11px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(preview.rackNumber, screenX, screenY);
    });

    // Draw racks
    racks.forEach(rack => {
      const dim = rackDimensionsMap.get(rack.rackNumber) || {
        width: 2.5, depth: 1.0, height: 3.0, verticals: 5, shelves: 5
      };

      const rotated = getRotatedDimensions(dim, rack.rotation);
      const screenX = offsetX + rack.x * scale;
      const screenY = offsetY + rack.y * scale;
      const screenWidth = rotated.width * scale;
      const screenDepth = rotated.depth * scale;

      const isSelected = selectedRacks.has(rack.rackNumber);
      const isAnchor = anchorRack === rack.rackNumber;

      // Determine rack color
      let fillColor = '#3b82f6';

      if (showHeatmap) {
        const intensity = heatmapLookup.get(rack.rackNumber) || 0;
        const r = Math.round(255 * intensity);
        const g = Math.round(255 * (1 - intensity) * 0.5);
        const b = Math.round(100 * (1 - intensity));
        fillColor = `rgb(${r}, ${g}, ${b})`;
      }

      const rackActivities = activityLookup.get(rack.rackNumber) || [];
      if (rackActivities.length > 0 && !showHeatmap) {
        fillColor = ACTIVITY_COLORS[rackActivities[0].activityType as ActivityType] || fillColor;
      }

      // Draw rack rectangle
      ctx.fillStyle = fillColor;
      ctx.globalAlpha = isSelected ? 1 : 0.8;
      ctx.fillRect(
        screenX - screenWidth / 2,
        screenY - screenDepth / 2,
        screenWidth,
        screenDepth
      );

      ctx.globalAlpha = 1;

      // Draw rack border
      ctx.strokeStyle = '#1e3a5f';
      ctx.lineWidth = 2;
      ctx.strokeRect(
        screenX - screenWidth / 2,
        screenY - screenDepth / 2,
        screenWidth,
        screenDepth
      );

      // Draw selection border (yellow for selected, green for anchor)
      if (isSelected || isAnchor) {
        ctx.strokeStyle = isAnchor ? '#22c55e' : '#fbbf24';
        ctx.lineWidth = 3;
        ctx.strokeRect(
          screenX - screenWidth / 2 - 2,
          screenY - screenDepth / 2 - 2,
          screenWidth + 4,
          screenDepth + 4
        );
      }

      // Draw rack label
      if (showLabels) {
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 14px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(rack.label || rack.rackNumber, screenX, screenY);

        ctx.font = '10px sans-serif';
        ctx.fillStyle = '#d1d5db';
        ctx.fillText(`${dim.verticals}×${dim.shelves}`, screenX, screenY + 14);
      }

      // Draw activity indicator
      if (rackActivities.length > 0 && editorState.showActivity) {
        const pulseSize = 8 + Math.sin(Date.now() / 200) * 3;
        ctx.beginPath();
        ctx.arc(screenX + screenWidth / 2 - 10, screenY - screenDepth / 2 + 10, pulseSize, 0, Math.PI * 2);
        ctx.fillStyle = ACTIVITY_COLORS[rackActivities[0].activityType as ActivityType] || '#22c55e';
        ctx.fill();
      }

      // Draw anchor icon
      if (isAnchor) {
        ctx.fillStyle = '#22c55e';
        ctx.font = 'bold 16px sans-serif';
        ctx.fillText('⚓', screenX - screenWidth / 2 + 10, screenY - screenDepth / 2 + 12);
      }
    });

    // Draw rubber band selection
    if (rubberBand.isActive) {
      const { startX, startY, currentX, currentY } = rubberBand;

      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(
        Math.min(startX, currentX),
        Math.min(startY, currentY),
        Math.abs(currentX - startX),
        Math.abs(currentY - startY)
      );
      ctx.setLineDash([]);

      ctx.fillStyle = 'rgba(59, 130, 246, 0.1)';
      ctx.fillRect(
        Math.min(startX, currentX),
        Math.min(startY, currentY),
        Math.abs(currentX - startX),
        Math.abs(currentY - startY)
      );
    }

    // Draw selection info overlay
    if (selectedRacks.size > 1) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
      ctx.fillRect(10, 10, 180, 32);
      ctx.fillStyle = '#ffffff';
      ctx.font = '13px sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${selectedRacks.size} reoler valgt`, 20, 26);
    }

    // Draw scale indicator
    ctx.fillStyle = '#9ca3af';
    ctx.font = '11px sans-serif';
    ctx.textAlign = 'left';
    const scaleBarLength = 5;
    const scaleBarPixels = scaleBarLength * scale;
    ctx.fillRect(20, height - 30, scaleBarPixels, 4);
    ctx.fillText(`${scaleBarLength}m`, 20, height - 38);

    // Draw keyboard shortcuts hint
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.fillRect(width - 170, height - 80, 160, 70);
    ctx.fillStyle = '#9ca3af';
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Ctrl+Klikk: Multi-valg', width - 160, height - 65);
    ctx.fillText('Dra i tomt: Områdevalg', width - 160, height - 52);
    ctx.fillText('R: Roter valgte', width - 160, height - 39);
    ctx.fillText('Delete: Slett valgte', width - 160, height - 26);

  }, [layout, editorState, canvasSize, rackSummaries, previewPositions, heatmapData, activities, rackDimensionsMap, heatmapLookup, activityLookup]);

  // Redraw on changes
  useEffect(() => {
    draw();
    const animationId = requestAnimationFrame(function animate() {
      if (editorState.showActivity && activities.length > 0) {
        draw();
      }
      requestAnimationFrame(animate);
    });
    return () => cancelAnimationFrame(animationId);
  }, [draw, editorState.showActivity, activities.length]);

  // Get rack at screen position
  const getRackAtPosition = (screenX: number, screenY: number): RackPosition | null => {
    const world = screenToWorld(
      screenX,
      screenY,
      canvasSize.width,
      canvasSize.height,
      layout.dimensions,
      editorState.zoom,
      editorState.panOffset
    );

    for (const rack of layout.racks) {
      const dim = rackDimensionsMap.get(rack.rackNumber) || { width: 2.5, depth: 1.0, height: 3, verticals: 5, shelves: 5 };
      const rotated = getRotatedDimensions(dim, rack.rotation);

      if (
        world.x >= rack.x - rotated.width / 2 &&
        world.x <= rack.x + rotated.width / 2 &&
        world.y >= rack.y - rotated.depth / 2 &&
        world.y <= rack.y + rotated.depth / 2
      ) {
        return rack;
      }
    }
    return null;
  };

  // Mouse handlers
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;

    // Right-click or middle-click for panning
    if (e.button === 1 || e.button === 2) {
      setIsPanning(true);
      setPanStart({ x: e.clientX, y: e.clientY });
      e.preventDefault();
      return;
    }

    if (e.button !== 0) return;

    const clickedRack = getRackAtPosition(screenX, screenY);
    const isMultiSelect = isMultiSelectKey(e);
    const isRangeSelect = isRangeSelectKey(e);

    if (editorState.activeTool === 'select') {
      if (clickedRack) {
        // Handle rack click with multi-select logic
        let newSelection: Set<string>;

        if (isMultiSelect) {
          newSelection = toggleSelection(editorState.selectedRacks, clickedRack.rackNumber);
        } else if (isRangeSelect && lastClickedRack) {
          newSelection = rangeSelect(editorState.selectedRacks, layout.racks, lastClickedRack, clickedRack.rackNumber);
        } else {
          newSelection = singleSelect(clickedRack.rackNumber);
        }

        onSelectionChange(newSelection);
        setLastClickedRack(clickedRack.rackNumber);

        // Start drag if rack is now selected
        if (newSelection.has(clickedRack.rackNumber)) {
          const world = screenToWorld(
            screenX,
            screenY,
            canvasSize.width,
            canvasSize.height,
            layout.dimensions,
            editorState.zoom,
            editorState.panOffset
          );

          const startPositions = new Map<string, { x: number; y: number }>();
          Array.from(newSelection).forEach(rackNum => {
            const rack = layout.racks.find(r => r.rackNumber === rackNum);
            if (rack) {
              startPositions.set(rackNum, { x: rack.x, y: rack.y });
            }
          });

          setDragState({
            isDragging: true,
            draggedRacks: Array.from(newSelection),
            startPositions,
            mouseStartX: world.x,
            mouseStartY: world.y
          });
        }
      } else if (!isMultiSelect) {
        // Start rubber band selection in empty area
        onEditorStateChange({
          ...editorState,
          rubberBand: startRubberBand(screenX, screenY)
        });
        onSelectionChange(new Set());
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (isPanning) {
      const dx = e.clientX - panStart.x;
      const dy = e.clientY - panStart.y;
      setPanStart({ x: e.clientX, y: e.clientY });
      onEditorStateChange({
        ...editorState,
        panOffset: {
          x: editorState.panOffset.x + dx,
          y: editorState.panOffset.y + dy
        }
      });
      return;
    }

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;

    // Update rubber band selection
    if (editorState.rubberBand.isActive) {
      onEditorStateChange({
        ...editorState,
        rubberBand: updateRubberBand(editorState.rubberBand, screenX, screenY)
      });

      // Calculate selected racks in rubber band area
      const { startX, startY } = editorState.rubberBand;
      const world1 = screenToWorld(
        startX,
        startY,
        canvasSize.width,
        canvasSize.height,
        layout.dimensions,
        editorState.zoom,
        editorState.panOffset
      );
      const world2 = screenToWorld(
        screenX,
        screenY,
        canvasSize.width,
        canvasSize.height,
        layout.dimensions,
        editorState.zoom,
        editorState.panOffset
      );

      const selectedInArea = getRacksInArea(
        layout.racks,
        rackDimensionsMap,
        world1.x,
        world1.y,
        world2.x,
        world2.y
      );

      onSelectionChange(new Set(selectedInArea));
      return;
    }

    // Handle multi-rack dragging
    if (dragState.isDragging && dragState.draggedRacks.length > 0) {
      const world = screenToWorld(
        screenX,
        screenY,
        canvasSize.width,
        canvasSize.height,
        layout.dimensions,
        editorState.zoom,
        editorState.panOffset
      );

      const deltaX = world.x - dragState.mouseStartX;
      const deltaY = world.y - dragState.mouseStartY;

      // Update all selected racks maintaining relative positions
      const updatedRacks = layout.racks.map(rack => {
        if (dragState.draggedRacks.includes(rack.rackNumber)) {
          const startPos = dragState.startPositions.get(rack.rackNumber);
          if (startPos) {
            return {
              ...rack,
              x: snapToGrid(startPos.x + deltaX, layout.gridUnit),
              y: snapToGrid(startPos.y + deltaY, layout.gridUnit)
            };
          }
        }
        return rack;
      });

      onLayoutChange({ ...layout, racks: updatedRacks });
    }
  };

  const handleMouseUp = () => {
    // End rubber band selection
    if (editorState.rubberBand.isActive) {
      onEditorStateChange({
        ...editorState,
        rubberBand: resetRubberBand()
      });
    }

    // End drag operation
    setDragState({
      isDragging: false,
      draggedRacks: [],
      startPositions: new Map(),
      mouseStartX: 0,
      mouseStartY: 0
    });

    setIsPanning(false);
  };

  const handleWheel = (e: React.WheelEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(0.3, Math.min(5, editorState.zoom * delta));
    onEditorStateChange({ ...editorState, zoom: newZoom });
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  return (
    <div
      ref={containerRef}
      className="w-full h-full bg-gray-900 rounded-lg overflow-hidden"
    >
      <canvas
        ref={canvasRef}
        width={canvasSize.width}
        height={canvasSize.height}
        className="cursor-crosshair"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        onContextMenu={handleContextMenu}
      />
    </div>
  );
};

export default Layout2DCanvas;
